(() => {
  const init = (root) => {
    if (root.hasAttribute('data-ready')) return;

    const source = root.querySelector(
      '[data-lp-bundle-ab-config]'
    );

    if (!source) return;

    let config;

    try {
      config = JSON.parse(source.textContent);
    } catch (error) {
      return;
    }

    const inputs = Array.from(
      root.querySelectorAll('[data-lp-kit-input]')
    );

    const bar = root.querySelector(
      '[data-lp-bundle-bar]'
    );

    const countEl = root.querySelector(
      '[data-lp-count]'
    );

    const noteEl = root.querySelector(
      '[data-lp-note]'
    );

    const priceEl = root.querySelector(
      '[data-lp-price]'
    );

    const totalEl = root.querySelector(
      '[data-lp-total]'
    );

    const cta = root.querySelector(
      '[data-lp-cta]'
    );

    const options = new Map(
      (config.options || []).map((option) => [
        option.key,
        option
      ])
    );

    /*
     * Include every A/B single variant AND every
     * original combination variant.
     */
    const variants = new Set(
      Array.from(options.values())
        .map((option) => String(option.variant))
        .filter(Boolean)
    );

    /*
     * Also include the original single variants.
     * This is important because if the customer had
     * the old $50 variant in the cart, it must be
     * removed when the A/B variant is selected.
     */
    if (config.originalSingleVariants) {
      Object.values(
        config.originalSingleVariants
      ).forEach((variant) => {
        variants.add(String(variant));
      });
    }

    /*
     * Add all four A/B variants.
     */
    if (config.abVariants) {
      Object.values(config.abVariants).forEach(
        (variant) => {
          variants.add(String(variant));
        }
      );
    }

    const syncsCart =
      config.mode === 'cart';

    const ctaAction =
      config.ctaAction || 'permalink';

    const ctaWritesCart =
      ctaAction !== 'permalink';

    const selected = () => {
      return inputs.filter(
        (input) => input.checked
      );
    };

    const keyOf = (chosen) => {
      return chosen
        .map((input) =>
          Number(input.dataset.slot)
        )
        .filter((slot) => slot > 0)
        .sort((a, b) => a - b)
        .join('+');
    };

    const inbound = () => {
      return new URLSearchParams(
        window.location.search
      );
    };

    const permalinkUrl = (variant) => {
      const params = inbound();

      params.set(
        'channel',
        'buy_button'
      );

      return `${config.cart}/${encodeURIComponent(
        variant
      )}:1?${params.toString()}`;
    };

    const withParams = (path) => {
      const params =
        inbound().toString();

      return params
        ? `${path}?${params}`
        : path;
    };

    const destination = (variant) => {
      if (ctaAction === 'cart') {
        return withParams(
          config.cart
        );
      }

      if (ctaAction === 'checkout') {
        return withParams(
          config.checkout
        );
      }

      return permalinkUrl(variant);
    };

    let current = null;
    let empty = true;
    let wanted = null;
    let queued = false;
    let chain = Promise.resolve();

    const ajax = (url, body) => {
      return fetch(url, {
        method: body
          ? 'POST'
          : 'GET',

        headers: body
          ? {
              'Content-Type':
                'application/json',
              Accept:
                'application/json'
            }
          : {
              Accept:
                'application/json'
            },

        body: body
          ? JSON.stringify(body)
          : undefined
      }).then((response) => {
        return response.ok
          ? response.json()
          : Promise.reject(response);
      });
    };

    /*
     * Get the selected option.
     *
     * The Liquid config already changes the four
     * single-kit variants, so combinations remain
     * exactly as they were.
     */
    const getOption = (chosen) => {
      const key = keyOf(chosen);

      return (
        options.get(key) ||
        null
      );
    };

    /*
     * Remove old bundle variants and add the
     * selected A/B/combo variant.
     */
    const reconcile = () => {
      const target = wanted;

      return ajax(
        `${config.cart}.js`
      ).then((cart) => {
        const updates = {};

        (cart.items || []).forEach(
          (item) => {
            const id = String(
              item.variant_id
            );

            if (
              variants.has(id) &&
              id !== target
            ) {
              updates[id] = 0;
            }
          }
        );

        if (target) {
          const line =
            (cart.items || []).find(
              (item) =>
                String(
                  item.variant_id
                ) === target
            );

          if (
            !line ||
            line.quantity !== 1
          ) {
            updates[target] = 1;
          }
        }

        if (
          !Object.keys(updates).length
        ) {
          return null;
        }

        return ajax(
          `${config.cartUpdate}.js`,
          {
            updates
          }
        );
      });
    };

    const sync = () => {
      if (
        !syncsCart ||
        queued
      ) {
        return chain;
      }

      queued = true;

      chain = chain
        .then(() => {
          queued = false;

          return reconcile();
        })
        .catch(() => {
          queued = false;
        });

      return chain;
    };

    const syncBar = () => {
      if (!bar) return;

      bar.toggleAttribute(
        'data-empty',
        empty
      );

      bar.toggleAttribute(
        'data-visible',
        !empty
      );
    };

    const update = () => {
      const chosen =
        selected();

      const option =
        getOption(chosen);

      const price =
        option
          ? option.price
          : '';

      /*
       * notePrice is the price advertised in the sticky
       * bar's "[price] gift card" note. For the four
       * single-kit A/B variants this is $50, while the
       * actual charged price (used for the total and the
       * checkout link) stays at $60. Combos have no
       * separate note price, so they fall back to price.
       */
      const notePrice =
        option
          ? (option.notePrice || option.price)
          : '';

      current = option;

      empty =
        chosen.length === 0;

      wanted =
        option &&
        option.variant
          ? String(
              option.variant
            )
          : null;

      if (countEl) {
        const template =
          (
            chosen.length === 1
              ? config.labelOne
              : config.labelMany
          ) || '';

        countEl.textContent =
          empty
            ? ''
            : template.replace(
                '[count]',
                chosen.length
              );

        countEl.hidden =
          empty;
      }

      if (priceEl) {
        priceEl.textContent =
          price;
      }

      if (totalEl) {
        totalEl.hidden =
          !price;
      }

      if (noteEl) {
        noteEl.textContent =
          (
            config.note || ''
          ).replace(
            '[price]',
            notePrice
          );

        noteEl.hidden =
          !notePrice;
      }

      if (cta) {
        cta.textContent =
          (
            empty
              ? config.ctaEmpty
              : config.cta
          ) || '';

        if (empty) {
          cta.href =
            config.anchor;

          cta.removeAttribute(
            'aria-disabled'
          );

        } else if (
          option &&
          option.variant
        ) {
          cta.href =
            destination(
              option.variant
            );

          cta.removeAttribute(
            'aria-disabled'
          );

        } else {
          cta.removeAttribute(
            'href'
          );

          cta.setAttribute(
            'aria-disabled',
            'true'
          );
        }
      }

      syncBar();
    };

    inputs.forEach(
      (input) => {
        input.addEventListener(
          'change',
          () => {
            update();
            sync();
          }
        );
      }
    );

    if (cta) {
      cta.addEventListener(
        'click',
        (event) => {
          if (
            !current ||
            !current.variant
          ) {
            return;
          }

          event.preventDefault();

          const go = () => {
            window.location.href =
              destination(
                current.variant
              );
          };

          if (!ctaWritesCart) {
            go();
            return;
          }

          Promise.race([
            sync(),

            new Promise(
              (resolve) =>
                setTimeout(
                  resolve,
                  1200
                )
            )
          ]).then(
            go,
            go
          );
        }
      );
    }

    update();

    root.setAttribute(
      'data-ready',
      ''
    );
  };

  const initAll = (scope) => {
    if (
      scope instanceof Element &&
      scope.matches(
        '.lp-bundle'
      )
    ) {
      /*
       * Only initialize this A/B config.
       * The original landing-bundle.js uses
       * [data-lp-bundle-config], so these two
       * systems remain separate.
       */
      if (
        scope.querySelector(
          '[data-lp-bundle-ab-config]'
        )
      ) {
        init(scope);
      }
    }

    scope
      .querySelectorAll(
        '.lp-bundle'
      )
      .forEach((element) => {
        if (
          element.querySelector(
            '[data-lp-bundle-ab-config]'
          )
        ) {
          init(element);
        }
      });
  };

  initAll(document);

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      if (
        event.target instanceof Element
      ) {
        initAll(event.target);
      }
    }
  );
})();